import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { PortraitFallback } from "../PortraitFallback";
import { CountUp } from "../Reveal";
import { daysLeft, publicFileUrl, coverSrcSet } from "../../lib/publicCampaigns";
import { campaignPath } from "../../lib/campaign";

const money = (cents) => `$${Math.round((cents || 0) / 100).toLocaleString()}`;
// Card is full-width on phones, half on tablets, a third of the 1280px shell on desktop.
const COVER_SIZES = "(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw";

/** Donor-facing card for a real, live campaign. Links to /campaign/:id. */
export const LiveCampaignCard = ({ c, animateBar = true }) => {
  const days = daysLeft(c.ends_at);
  const cover = publicFileUrl(c.cover_file_id, 960);
  return (
    <Link
      to={campaignPath(c)}
      data-testid={`live-card-${c.id}`}
      className="group flex h-full flex-col rounded-md border border-border bg-bone transition-shadow hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-t-md">
          {cover ? (
            <img src={cover} srcSet={coverSrcSet(c.cover_file_id)} sizes={COVER_SIZES} alt={`${c.first_name}'s campaign`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <PortraitFallback name={c.first_name} school={c.school || ""} gradYear={c.grad_year || ""} nameSize="text-[clamp(3rem,7vw,5.5rem)]" className="transition-transform duration-700 group-hover:scale-105" />
          )}
          <span className="absolute left-4 top-4 rounded-full bg-sand/90 px-3 py-1 text-[13px] font-semibold text-ink backdrop-blur" data-testid={`live-card-pct-${c.id}`}>
            {c.pct}% funded
          </span>
        </div>
      <div className="flex flex-1 flex-col p-8">
        <h3 className="font-serif text-3xl leading-none text-ink">{c.first_name}</h3>
        <p className="mt-2 text-[13px] text-olive" data-testid={`live-card-school-${c.id}`}>{[c.school, c.program].filter(Boolean).join(" · ")}</p>
        {c.headline ? (
          <p className="mt-5 flex-1 font-serif text-3xl italic leading-snug text-ink">&ldquo;{c.headline}&rdquo;</p>
        ) : (
          <p className="mt-5 flex-1 text-base leading-relaxed text-olive [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">{c.excerpt}</p>
        )}

        <div className="mt-8">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            {animateBar ? (
              <motion.div className="h-full rounded-full bg-tangerine" initial={{ width: 0 }} whileInView={{ width: `${c.pct}%` }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.15 }} />
            ) : (
              <div className="h-full rounded-full bg-tangerine" style={{ width: `${c.pct}%` }} />
            )}
          </div>
          <div className="mt-2 flex items-center justify-between text-[13px]">
            <span className="font-semibold text-ink"><CountUp to={Math.round(c.raised_cents / 100)} prefix="$" /> given</span>
            <span className="text-olive">of {money(c.goal_cents)}</span>
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-[13px] text-olive" data-testid={`live-card-days-${c.id}`}>
            <Clock className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
            <span>{days > 0 ? `${days} ${days === 1 ? "day" : "days"} left` : "Closing"}</span>
            {c.category && <span className="capitalize" data-testid={`live-card-category-${c.id}`}>· {c.category}</span>}
          </p>
        </div>

        <span className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink underline-offset-4 group-hover:underline">
          View campaign
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
        </span>
      </div>
    </Link>
  );
};
